<template>
  <section>
    <div
      class="hfw"
      :class="[alignVertical, backgroundColorClass]"
    >
      <div
        class="hfw-inner"
        :class="alignHorizontal"
      >
        <kf-heading
          v-if="title"
          tag="h1"
          class="hfw-title"
        >
          {{ title }}
        </kf-heading>
      </div>
      <img
        class="hfw-image"
        :src="headerImage"
        :alt="imageAlt"
        aria-hidden="true"
      >
      <div class="hfw-curve">
        <kf-curve
          :curve-is-big="true"
          :curve-svg-color="backgroundColor"
        />
      </div>
    </div>
    <div
      v-if="isLocatorSlotFilled"
      class="hfw-locator-row"
    >
      <kf-row>
        <kf-container container-size="lg">
          <div class="hfw-locator-container">
            <slot name="locator" />
          </div>
        </kf-container>
      </kf-row>
    </div>
  </section>
</template>

<script>
import KfCurve from '@/components/base/curve/Curve.vue';
import KfHeading from '@/components/base/heading/Heading.vue';
import KfContainer from '@/components/layout/container/Container.vue';
import KfRow from '@/components/layout/row/Row.vue';
import TailwindMixin from '@/mixins/TailwindMixin';

export default {
  name: 'KfHeaderProjectDetail',
  components: {
    KfContainer,
    KfCurve,
    KfHeading,
    KfRow,
  },
  mixins: [TailwindMixin],
  props: {
    alignmentHorizontal: {
      type: String,
      default: '',
    },
    alignmentVertical: {
      type: String,
      default: '',
    },
    imageSrcMobile: {
      type: String,
      default: '',
    },
    imageSrcLarge: {
      type: String,
      default: '',
    },
    imageAlt: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    }
  },
  computed: {
    alignHorizontal() {
      return {
        [`is-${this.alignmentHorizontal}-aligned-horizontal`]: this.alignmentHorizontal,
      };
    },
    alignVertical() {
      return {
        [`is-${this.alignmentVertical}-aligned-vertical`]: this.alignmentVertical,
      };
    },
    headerImage() {
      if (this.doesWindowHaveMinWidthXs) {
        return this.imageSrcLarge;
      }
      return this.imageSrcMobile;
    },
    backgroundColorClass() {
      return this.backgroundColor
        ? `has-color-${this.backgroundColor.toLowerCase().replace(' ', '-')}`
        : null;
    },
    isLocatorSlotFilled() {
      return !!this.$slots['locator'];
    },
  },
};
</script>

<style lang="postcss">
.hfw {
  @apply w-full flex flex-col justify-center relative;
  min-height: 500px;

  @screen md {
    @apply pt-0 pb-8;
    min-height: 670px;
  }

  &.is-top-aligned-vertical {
    @apply justify-start;
  }

  &.is-middle-aligned-vertical {
    @apply justify-center;
  }

  &.is-bottom-aligned-vertical {
    @apply justify-end;
  }

  &-inner {
    @apply relative flex flex-col container pb-0 items-start px-4;
    z-index: 1;

    &.is-center-aligned-horizontal {
      @apply items-center;

      .hfw-title {
        @apply text-center;
      }
    }

    &.is-right-aligned-horizontal {
      @apply items-end;

      .hfw-title {
        @apply text-right;
      }
    }
  }

  &-stage {
    @apply relative bg-white px-8 py-4 rounded;
    box-shadow: 0 4px 20px rgba(54, 25, 5, 0.12);

    @screen md {
      @apply px-28 py-14;
    }
  }

  &-image {
    @apply absolute w-full h-full object-cover z-0 top-0 left-0;
    object-position: 50% 50%;
  }

  &-title {
    @apply text-white font-playfair text-9xl mb-8 font-bold text-left;
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.65);

    @screen md {
      @apply text-left;
    }

    @screen xl {
      @apply text-10xl;
    }
  }

  &-locator-container {
    @apply relative w-full pt-7 pb-7 z-10 mx-auto box-content;
    max-width: 600px;

    @screen lg {
      @apply bg-white rounded-smd -mt-40 mr-0 p-8;
      box-shadow: 0 4px 20px rgb(54, 25, 5, 0.12);
    }
  }

  &-curve {
    @apply block absolute left-0 -bottom-1 w-full overflow-hidden z-10;
  }
}

.hfw-locator-row {
  @apply relative pb-14;

  &:after {
    @apply absolute bg-gray-50 top-0 left-0 w-full h-full;
    content: '';
    z-index: -1;
  }
}
</style>
